/**
 * WritingPracticeTile — dashboard tile showing recent completed writing
 * attempts (show-not-tell/TS-012).
 *
 * Implements show-not-tell/TS-012 (T4, AC-TS012.1, AC-TS012.3). Fetches the
 * bounded, recency-ordered recent-completed-sessions list (API-005) via
 * React Query, renders a newest-first list of rows with compact success-
 * signal chips, a friendly empty state for a new user, and degrades
 * fail-safe (the SAME empty state, never an error banner) when the fetch
 * fails (edge case 'Recent read fails during dashboard assembly').
 *
 * Reuses the EmptyState / min-h-[44px] tap-target row / focus-visible ring /
 * formatRelativeTime scaffolding cloned from StudentDashboardPage's
 * "Recent sessions" section (frontend/src/pages/StudentDashboardPage.tsx).
 *
 * IMPORTANT: this component ONLY reads. The post-completion refresh
 * (optimistic prime + delayed conditional invalidate of THIS EXACT
 * queryKey, ['writing','recent']) lives in useWritingSession.complete
 * (T5, TO-LL-026) — never here.
 *
 * Enforces TO-LL-036: never throws; useQuery's isError path renders the
 * SAME friendly state as the empty case — the tile's absence/failure must
 * never break the rest of the dashboard.
 *
 * show-not-tell/US-011 (AC-011.2): each row navigates to
 * ROUTES.WRITING_PRACTICE with a `?sessionId=` query param carrying THAT
 * row's own id, so WritingPracticePage.openSession() reopens the SPECIFIC
 * clicked session (GET {id}, API-006) rather than the bare start screen.
 * Closes the scoping gap TS-012's dev flagged (that story's guide never
 * listed a dedicated viewer component in T4/T5's file_structure).
 *
 * show-not-tell/TS-014 (T5, DD-5, AC-TS014.4, AC-TS014.5): rows are rendered
 * via the shared WritingSessionRow component (extracted verbatim from this
 * file's former inline markup) so the tile and the new full history page
 * (/writing-practice/history) never drift; a "See all my writing sessions"
 * link is shown below the list ONLY when the student has any sessions,
 * navigating to ROUTES.WRITING_PRACTICE_HISTORY.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/EmptyState';
import { WritingSessionRow } from '@/components/WritingSessionRow';
import { getRecentWritingSessions } from '@/services/showNotTell';
import { ROUTES } from '@/constants/routes';

export function WritingPracticeTile() {
  // T5 primes/invalidates this EXACT queryKey on the completion transition —
  // the two tasks share this contract verbatim (do not rename in isolation).
  const { data, isLoading, isError } = useQuery({
    queryKey: ['writing', 'recent'],
    queryFn: getRecentWritingSessions,
  });

  const sessions = data?.sessions ?? [];

  return (
    <section aria-label="Writing practice activity" className="mb-4 md:mb-6">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold text-otter-earth-800">
          Writing practice
        </h2>
      </div>

      <Card>
        <CardContent className="p-4">
          {isLoading ? (
            // Lightweight skeleton — never a blocking spinner.
            <div className="space-y-2.5" aria-hidden="true">
              <div className="h-11 animate-pulse rounded-2xl bg-otter-sand/40" />
              <div className="h-11 animate-pulse rounded-2xl bg-otter-sand/40" />
            </div>
          ) : isError || sessions.length === 0 ? (
            // TO-LL-036: the SAME friendly state serves the new-user empty
            // case (AC-TS012.3) and the fail-safe fetch-error degrade (edge
            // case) — never a raw error banner.
            <EmptyState message="No writing practice yet — try your first one!" />
          ) : (
            <>
              <ul className="space-y-2.5" role="list">
                {sessions.map((row) => (
                  <li key={row.sessionId}>
                    <WritingSessionRow row={row} />
                  </li>
                ))}
              </ul>
              {/* show-not-tell/TS-014 (T5, AC-TS014.4): shown only when the
                  student has any sessions; hidden in loading/empty/error
                  states above. TO-LL-013: route constant, never a literal. */}
              <div className="mt-3 text-center">
                <Link
                  to={ROUTES.WRITING_PRACTICE_HISTORY}
                  className="inline-flex min-h-[44px] items-center justify-center rounded-lg px-2 py-1.5 text-sm font-bold text-pebbles-500 transition-colors hover:bg-otter-sand/40 hover:text-otter-earth-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-otter-orange-400"
                >
                  See all my writing sessions
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
